import React, { useState, useEffect } from 'react';
import apiClient from '../../services/apiClient';
import StakeTable from './StakeTable';
import { Button } from '../../components/ui';
import appConfig from '../../config';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const fetchGroupsAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/groups', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};

const fetchCategoriesAPI = async () => {
  const token = localStorage.getItem('token');
  const res = await apiClient.get('/api/game-engine/admin/categories', { headers: { Authorization: `Bearer ${token}` } });
  return res.data.success ? res.data.data : [];
};



interface Aggregator {
  id: number;
  name: string;
}

interface Provider {
  id: number;
  name: string;
  developer_is_enabled?: boolean;
}

interface Game {
  id: number;
  provider_id: number;
  provider_name?: string;
  name: string;
  game_code: string;
  thumbnail_url: string;
  is_active: boolean;
  is_featured: boolean;
  custom_thumbnail_url?: string;
  image_type?: 'api' | 'custom';
}

const fetchProvidersAPI = async (): Promise<Provider[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/providers`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const fetchAggregatorsAPI = async (): Promise<Aggregator[]> => {
  const token = localStorage.getItem("token");
  const res = await apiClient.get(`/api/game-engine/admin/aggregators`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  return res.data.success ? res.data.data : [];
};

const GamesManagement: React.FC = () => {
  const queryClient = useQueryClient();
  
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [filterProviderId, setFilterProviderId] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterGroupId, setFilterGroupId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const provId = params.get('provider_id');
    if (provId) setFilterProviderId(provId);
    
    const catId = params.get('category_id');
    if (catId) setFilterCategoryId(catId);

    const grpId = params.get('group_id');
    if (grpId) setFilterGroupId(grpId);
  }, []);

  const { data: gamesData = { data: [], total: 0 }, isLoading: gamesLoading } = useQuery({
    queryKey: ['games', page, searchTerm, filterProviderId, filterCategoryId, filterGroupId, filterStatus, limit],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      const offset = (page - 1) * limit;
      let url = `/api/game-engine/admin/games?limit=${limit}&offset=${offset}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (filterProviderId) url += `&provider_id=${filterProviderId}`;
      if (filterCategoryId) url += `&category_id=${filterCategoryId}`;
      if (filterGroupId) url += `&group_id=${filterGroupId}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const res = await apiClient.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return res.data.success ? { data: res.data.data, total: res.data.total } : { data: [], total: 0 };
    }
  });

  const games = gamesData.data;
  const total = gamesData.total;

  const { data: providers = [], isLoading: providersLoading } = useQuery({
    queryKey: ['providers'],
    queryFn: fetchProvidersAPI
  });

  const { data: aggregators = [], isLoading: aggregatorsLoading } = useQuery({
    queryKey: ['aggregators'],
    queryFn: fetchAggregatorsAPI
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: fetchCategoriesAPI
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Game>>({});
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [syncAggId, setSyncAggId] = useState<number>(0);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const { data: groups = [] } = useQuery({ queryKey: ['groups'], queryFn: fetchGroupsAPI });
  const [selectedAssignGroupId, setSelectedAssignGroupId] = useState<number | ''>('');

  const assignToGroupMutation = useMutation({
    mutationFn: async () => {
      if (!selectedAssignGroupId) throw new Error('No group selected');
      const token = localStorage.getItem('token');
      return apiClient.post(`/api/game-engine/admin/groups/${selectedAssignGroupId}/assign-games`, { game_ids: selectedIds }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      toast.success(res.data.message || 'Games successfully assigned to group!');
      setSelectedIds([]); // clear selection
      setSelectedAssignGroupId('');
    },
    onError: () => toast.error('Failed to assign games to group')
  });


  
  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Game>) => {
      const token = localStorage.getItem("token");
      return apiClient.put(`/api/game-engine/admin/games/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
      setSnackbar("Game updated successfully");
      setTimeout(() => setSnackbar(null), 3000);
      closeModal();
    },
    onError: (err) => {
      console.error("Error saving game:", err);
      toast.error("Failed to save game");
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const token = localStorage.getItem("token");
      return apiClient.delete(`/api/game-engine/admin/games/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
      setSnackbar("Game deleted successfully");
      setTimeout(() => setSnackbar(null), 3000);
    },
    onError: (err) => {
      console.error("Error deleting game:", err);
      toast.error("Failed to delete game");
    }
  });

  const syncMutation = useMutation({
    mutationFn: async (aggregator_id: number) => {
      const token = localStorage.getItem("token");
      return apiClient.post(`/api/game-engine/admin/games/sync`, { aggregator_id }, { headers: { Authorization: `Bearer ${token}` } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
      setSnackbar(res.data.message);
      setTimeout(() => setSnackbar(null), 4000);
      setIsSyncModalOpen(false);
    },
    onError: (err) => {
      console.error("Error syncing games:", err);
      toast.error("Failed to sync games");
    }
  });

  const openModal = (game: Game) => {
    setFormData(game);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({});
    setSelectedImageFile(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let finalData = { ...formData };
    
    if (selectedImageFile) {
      const formDataPayload = new FormData();
      formDataPayload.append('image', selectedImageFile);
      try {
        const token = localStorage.getItem('token');
        const res = await apiClient.post('/api/game-engine/admin/games/upload-image', formDataPayload, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        if (res.data.success) {
          finalData.custom_thumbnail_url = res.data.url;
        } else {
          return toast.error('Failed to upload image');
        }
      } catch (err) {
        return toast.error('Error uploading image');
      }
    }
    
    saveMutation.mutate(finalData);
  };

  const handleDelete = (id: number) => {
    if (!window.confirm("Are you sure you want to delete this game?")) return;
    deleteMutation.mutate(id);
  };

  const handleSync = () => {
    if (!syncAggId) return toast.error("Please select an aggregator");
    syncMutation.mutate(syncAggId);
  };

  const totalPages = Math.ceil(total / limit);

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'thumbnail_url', 
      label: 'API Image',
      render: (val: string) => val ? <img src={val} alt="thumbnail" className="h-9 w-9 rounded-md object-cover shadow-sm border border-slate-700" /> : <div className="h-9 w-9 rounded-md bg-slate-800 flex items-center justify-center text-xs text-slate-500">N/A</div>
    },
    { 
      key: 'custom_thumbnail_url', 
      label: 'Custom Image',
      render: (val: string) => val ? <img src={`${appConfig.baseURL}${val}`} alt="custom" className="h-9 w-9 rounded-md object-cover shadow-sm border border-slate-700" /> : <div className="h-9 w-9 rounded-md bg-slate-800 flex items-center justify-center text-xs text-slate-500">N/A</div>
    },
    { 
      key: 'image_type', 
      label: 'Image Type',
      render: (val: string) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${val === 'custom' ? 'bg-indigo-500/20 text-indigo-400 border border-indigo-500/30' : 'bg-slate-700 text-slate-300'}`}>
          {val || 'api'}
        </span>
      )
    },
    { 
      key: 'name', 
      label: 'Game Name',
      render: (val: string) => <span className="font-bold text-white">{val}</span>
    },
    { 
      key: 'provider_name', 
      label: 'Provider',
      render: (val: string, game: Game) => val ? (
        <span className="text-slate-300 font-medium">
          {val} <span className="text-slate-500 font-mono text-[10px]">(ID: {game.provider_id})</span>
        </span>
      ) : <span className="text-slate-500">-</span>
    },
    { 
      key: 'game_code', 
      label: 'Game Code',
      render: (val: string) => <span className="text-slate-400 text-xs font-mono">{val}</span>
    },
    { 
      key: 'is_active', 
      label: 'Status',
      render: (val: boolean) => val ? (
        <span className="bg-green-500/10 text-green-400 border border-green-500/20 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider">Active</span>
      ) : (
        <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider">Inactive</span>
      )
    },
    { 
      key: 'is_featured', 
      label: 'Featured',
      render: (val: boolean) => val ? (
        <span className="text-yellow-500 font-bold flex items-center gap-1 text-xs"><svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg> Yes</span>
      ) : (
        <span className="text-slate-500 text-xs font-medium">No</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      textAlign: 'right' as const,
      render: (_: any, game: Game) => (
        <div className="flex justify-end gap-2">
          <button onClick={() => openModal(game)} className="text-sky-400 hover:text-sky-300 hover:bg-sky-400/10 px-2.5 py-1 rounded transition-colors text-xs font-medium">Edit</button>
          <button onClick={() => handleDelete(game.id)} className="text-red-400 hover:text-red-300 hover:bg-red-400/10 px-2.5 py-1 rounded transition-colors text-xs font-medium">Delete</button>
        </div>
      )
    }
  ];

  return (
    <>
      {snackbar && (
        <div className="fixed top-20 right-6 z-50 text-white bg-green-500 py-2 px-4 rounded-md shadow-md text-sm font-medium animate-in fade-in slide-in-from-top-2">
          {snackbar}
        </div>
      )}

      <StakeTable
        title="Games Management"
        columns={columns}
        data={games}
        searchPlaceholder="Search games by name or code..."
        onSearch={(val) => { setSearchTerm(val); setPage(1); }}
        page={page}
        totalPages={totalPages}
        total={total}
        limit={limit}
        onLimitChange={setLimit}
        onPageChange={setPage}
        selectable={true}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        emptyMessage="No games found. Please sync games from API."
        isLoading={gamesLoading}
        bottomActions={
          <div className="flex flex-wrap items-center gap-2.5 w-full">
            <select
              className="bg-slate-900 border border-slate-700 text-slate-300 text-xs sm:text-sm rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 sm:py-2 px-2.5 sm:px-3 transition-colors cursor-pointer flex-1 sm:flex-initial min-w-[110px]"
              value={filterProviderId}
              onChange={(e) => { setFilterProviderId(e.target.value); setPage(1); }}
            >
              <option value="" className="bg-slate-900 text-slate-400">All Providers</option>
              {providers.filter((p: any) => p.developer_is_enabled !== false).map((p: any) => (
                <option key={p.id} value={p.id}>{p.name} (ID: {p.id})</option>
              ))}
            </select>

            <select
              className="bg-slate-900 border border-slate-700 text-slate-300 text-xs sm:text-sm rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 sm:py-2 px-2.5 sm:px-3 transition-colors cursor-pointer flex-1 sm:flex-initial min-w-[110px]"
              value={filterCategoryId}
              onChange={(e) => { setFilterCategoryId(e.target.value); setPage(1); }}
            >
              <option value="" className="bg-slate-900 text-slate-400">All Categories</option>
              {categories.map((c: any) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              className="bg-slate-900 border border-slate-700 text-slate-300 text-xs sm:text-sm rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 sm:py-2 px-2.5 sm:px-3 transition-colors cursor-pointer flex-1 sm:flex-initial min-w-[110px]"
              value={filterGroupId}
              onChange={(e) => { setFilterGroupId(e.target.value); setPage(1); }}
            >
              <option value="" className="bg-slate-900 text-slate-400">All Groups</option>
              {groups.map((g: any) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>

            <select
              className="bg-slate-900 border border-slate-700 text-slate-300 text-xs sm:text-sm rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 sm:py-2 px-2.5 sm:px-3 transition-colors cursor-pointer flex-1 sm:flex-initial min-w-[110px]"
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            >
              <option value="">All Status</option>
              <option value="true" className="bg-slate-900 text-slate-200">Active</option>
              <option value="false" className="bg-slate-900 text-slate-200">Inactive</option>
            </select>

            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 bg-slate-800/50 p-1.5 rounded-lg border border-slate-700 w-full sm:w-auto">
                <span className="text-xs text-slate-300 font-medium whitespace-nowrap">{selectedIds.length} selected</span>
                <select
                  className="bg-slate-900 border border-slate-700 text-slate-300 text-xs rounded-md shadow-sm hover:bg-slate-800 focus:outline-none focus:border-indigo-500 py-1.5 px-2 transition-colors cursor-pointer"
                  value={selectedAssignGroupId}
                  onChange={(e) => setSelectedAssignGroupId(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="" className="bg-slate-900 text-slate-400">Group...</option>
                  {groups.map((g: any) => (
                    <option key={g.id} value={g.id} className="bg-slate-900 text-slate-200">{g.name}</option>
                  ))}
                </select>
                <Button 
                  variant="success" 
                  size="xs"
                  disabled={!selectedAssignGroupId || selectedIds.length === 0} 
                  loading={assignToGroupMutation.isPending}
                  onClick={() => assignToGroupMutation.mutate()}
                >
                  Assign
                </Button>
              </div>
            )}
          </div>
        }
        actions={
          <Button variant="success" size="sm" onClick={() => setIsModalOpen(true)}>+ Add Game</Button>
        }
      />

      {/* Sync Modal */}
      {isSyncModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm z-50 p-4">
          <div className="bg-slate-900 border-slate-700/60 shadow-2xl border rounded-xl p-6 w-full max-w-[400px] max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4 text-white text-center">Sync Games</h2>
            <p className="text-gray-300 text-sm mb-4 text-center">Select an aggregator to fetch the latest games list. This may take a few minutes.</p>
            <div className="mb-6">
              <select 
                value={syncAggId} 
                onChange={(e) => setSyncAggId(parseInt(e.target.value))}
                className="w-full p-2.5 bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border rounded-lg text-white outline-none"
              >
                <option value={0} disabled>Select Aggregator</option>
                {aggregators.map((agg: any) => (
                  <option key={agg.id} value={agg.id}>{agg.name} (ID: {agg.id})</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setIsSyncModalOpen(false)} className="bg-slate-800 hover:bg-slate-700  text-white px-5 py-2 rounded-lg transition-colors text-sm font-semibold">Cancel</button>
              <button onClick={handleSync} className="bg-[#646ECD] hover:bg-[#525ab5] text-white px-5 py-2 rounded-lg transition-colors text-sm font-semibold">Start Sync</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm z-50 p-4">
          <div className="bg-slate-900 border-slate-700/60 shadow-2xl border rounded-xl p-6 w-full max-w-[500px] max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6 text-white text-center">Edit Game</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Game Name</label>
                <input required type="text" value={formData.name || ''} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-2.5 bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border rounded-lg text-white outline-none" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">API Image URL</label>
                <input type="text" value={formData.thumbnail_url || ''} onChange={e => setFormData({...formData, thumbnail_url: e.target.value})} className="w-full p-2.5 bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border rounded-lg text-white outline-none" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Custom Image (URL or Upload)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Image URL"
                    value={selectedImageFile ? selectedImageFile.name : (formData.custom_thumbnail_url || '')}
                    onChange={e => { setFormData({ ...formData, custom_thumbnail_url: e.target.value }); setSelectedImageFile(null); }}
                    className="flex-1 p-2.5 bg-slate-950/50 border border-slate-700/50 rounded-lg text-white focus:outline-none focus:border-indigo-500 text-sm"
                  />
                  <div className="relative w-28">
                    <input type="file" id="custom_img_upload" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={(e) => { if (e.target.files && e.target.files[0]) { setSelectedImageFile(e.target.files[0]); toast.info("Image selected. Save to upload."); } }} accept="image/*" />
                    <div className="w-full h-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 text-sm hover:bg-slate-700 transition-colors pointer-events-none whitespace-nowrap flex items-center justify-center font-semibold">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      Upload
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Image Type (Which to display)</label>
                <select 
                  value={formData.image_type || 'api'} 
                  onChange={e => setFormData({ ...formData, image_type: e.target.value as 'api' | 'custom' })} 
                  className="w-full p-2.5 bg-slate-950/50 border-slate-700/50 focus:border-indigo-500 border rounded-lg text-white outline-none"
                >
                  <option value="api" className="bg-slate-900 text-slate-200">API Image</option>
                  <option value="custom" className="bg-slate-900 text-slate-200">Custom Image</option>
                </select>
              </div>

              <div className="flex gap-6 mt-4">
                <div className="flex items-center">
                  <input type="checkbox" id="isActive" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" />
                  <label htmlFor="isActive" className="ml-2 text-sm font-medium text-gray-300">Active</label>
                </div>
                <div className="flex items-center">
                  <input type="checkbox" id="isFeatured" checked={formData.is_featured} onChange={e => setFormData({...formData, is_featured: e.target.checked})} className="w-4 h-4 text-yellow-500 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500" />
                  <label htmlFor="isFeatured" className="ml-2 text-sm font-medium text-gray-300">Featured</label>
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-8">
                <button type="button" onClick={closeModal} className="bg-slate-800 hover:bg-slate-700  text-white px-5 py-2 rounded-lg transition-colors">Cancel</button>
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg transition-colors">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default GamesManagement;
